// Client-side sealing for control-plane secrets (D-057, milestone 2).
//
// A broker/data-feed key is encrypted IN THE BROWSER to the trading box's
// public key, so the backend relay only ever carries ciphertext it cannot
// read — only the box's private key can open it. No dependency: this uses the
// browser's built-in Web Crypto (SubtleCrypto), not a bundled NaCl.
//
// Scheme "ta-seal-v1" — ECIES over P-256:
//   ephemeral ECDH(P-256) -> HKDF-SHA256 -> AES-256-GCM.
// The agent decrypts with the matching P-256 private key using Python's
// `cryptography` (see services/api/agent/tickahead_agent.py). The two sides MUST
// agree on curve, salt, info, and point encoding — keep them in lockstep.

const SALT = new Uint8Array(32); // fixed 32 zero bytes; must match the agent
const INFO = new TextEncoder().encode("ta-seal-v1");

// Web Crypto wants BufferSource; TS's newer typed-array generics complain about
// ArrayBufferLike vs ArrayBuffer variance. This coercion is safe here.
const bs = (x: Uint8Array | ArrayBuffer): BufferSource => x as BufferSource;

function toB64(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const b of arr) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

export interface SealedBlob {
  v: string; // scheme version
  epk: string; // ephemeral P-256 public key, base64 raw uncompressed point
  iv: string; // AES-GCM nonce, base64 (12 bytes)
  ct: string; // ciphertext + tag, base64
}

/** Seal `plaintext` to a box's P-256 public key (base64 of the raw
 * uncompressed point, 65 bytes). Returns a blob safe to hand to the relay. */
export async function seal(recipientPublicKeyB64: string, plaintext: string): Promise<SealedBlob> {
  const subtle = crypto.subtle;
  const recipient = await subtle.importKey(
    "raw",
    bs(fromB64(recipientPublicKeyB64)),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  const eph = await subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]);
  const shared = await subtle.deriveBits({ name: "ECDH", public: recipient }, eph.privateKey, 256);
  const hkdfKey = await subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  const aesKey = await subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: bs(SALT), info: bs(INFO) },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await subtle.encrypt({ name: "AES-GCM", iv: bs(iv) }, aesKey, bs(new TextEncoder().encode(plaintext)));
  const epkRaw = await subtle.exportKey("raw", eph.publicKey);
  return { v: "ta-seal-v1", epk: toB64(epkRaw), iv: toB64(iv), ct: toB64(ct) };
}
